// Features: M004-F0713, M004-F0714. Tests: T-M004-0206, T-M004-0207, T-M004-0209.
//
// The transaction history drawer (design M004-F0712-F0716 section 2). READ-ONLY: it offers links to the real controls,
// never a second copy of a write. Core's sentences (headline, from, next_action, conditions[], can.*.reason, summary) are
// shown VERBATIM (ACCT-TX-011); the WebUI authors only the words in tx/txStages.js and the status sentences below (2.7).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useDismissOnOutsideClick from '../hooks/useDismissOnOutsideClick';
import StepUpDialog from './auth/StepUpDialog';
import { txHistoryApi } from '../services/api';
import { getStepUpChallengeFromError } from '../utils/stepUp';
import {
  creditNoteOffered, ledgerWritten, applyLedgerState, classifyHistoryError, mergeChildren, normaliseLedgerOnlyHistory, normaliseTxHistory, remainingToCreditMinor, unwrap,
} from '../services/txHistoryAdapter';
import { useMoneyActionGate } from './ledger/useMoneyActionGate';
import TxBillMore from './tx/TxBillMore';
import TxCreditNoteForm from './tx/TxCreditNoteForm';
import TxTimeline, { TxInvoiceAndLedger, formatTxTime } from './tx/TxTimeline';
import { TX_NONE_YET } from './TxChip';
import { amountText, conditionBadge, FAILURE_STAGES, stageLabel, stageSentence } from './tx/txStages';
import { displayTxId, txIdFromHandOffId, wellFormedTxId } from '../utils/transactionId';
import './TxHistoryDrawer.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// `window` carries WALL-CLOCK strings ("2026-10-08T20:00") in the given zone: never converted, only laid out.
export function formatWindow(window) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(window?.start || '');
  if (!m) return '';
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const end = /T(\d{2}:\d{2})/.exec(window?.end || '');
  return `${WEEKDAYS[day.getUTCDay()]} ${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}, ${m[4]}${end ? `-${end[1]}` : ''}${window.timezone ? ` ${window.timezone}` : ''}`;
}

const SENTENCES = Object.freeze({
  loading: (tx) => `Loading the history of ${tx}...`,
  slow: 'This is taking longer than usual.',
  failed: "Core could not load this transaction's history. Nothing has changed.",
  forbidden: "You don't have permission to see this transaction's history. It needs billing access for this organisation.",
  notFound: (tx) => `No transaction ${tx} was found in this organisation. Check the number, and that the right organisation is selected.`,
  notFoundLedgerHint: 'If this was created in the Ledger, paste its full id.',
  ambiguous: (tx) => `More than one transaction in this organisation starts with ${tx}. Choose one:`,
  badKey: 'That is not a transaction number. Use TXN- followed by its code, for example TXN-0018-5JHWEU1ES8X, or the full id.',
  noOrigin: 'Not available without Core.',
});

const fallbackIntro = (why) => `Core's part of this history could not be shown${why === 'forbidden' ? ": you don't have billing access" : ': Core could not load it'}. This is the Ledger's part.`;

const ACTION_ROWS = [
  { key: 'move', stages: ['advice', 'draft'], label: (stage) => (stage === 'draft' ? 'Move back to Advice' : 'Move to invoice'), to: (org) => `/app/ledger/${org}/members` },
  { key: 'edit_amount', stages: ['advice', 'draft'], label: () => 'Change the amount', to: (org) => `/app/ledger/${org}/members` },
  { key: 'release', stages: ['awaiting_approval', 'returned_for_reprice'], label: () => 'Approve and send', to: (org) => `/app/bookings?tab=billing&org=${org}` },
  { key: 'retry', stages: ['dispatch_failed'], label: () => 'Retry sending', to: (org) => `/app/bookings?tab=billing&org=${org}` },
];

function PriceText({ value }) {
  const [all, setAll] = useState(false);
  const long = value.length > 300;
  return (
    <span data-testid="tx-raw-price-text">
      {'Price text in the calendar event: "'}{long && !all ? `${value.slice(0, 300)}...` : value}{'"'}
      {long && !all ? <> <button type="button" className="tx-link-button" onClick={() => setAll(true)}>Show all</button></> : null}
    </span>
  );
}

// Spec 1.5.0 (ACCT-TX-022): origin.source `unknown` means provenance cannot be determined (a TX backfilled from billing records
// whose booking is gone). It is a declared gap and is NEVER shown as "Booked in the app".
export function originSourceText(o) {
  if (o.source === 'unknown') return 'Origin unknown';
  if (o.source === 'ledger' || o.minted_by === 'ledger') return 'Created in the Ledger';
  return o.source === 'gcal' ? 'Booked in Google Calendar' : 'Booked in the app';
}

function OriginSection({ model }) {
  const o = model.origin;
  if (!o) return <p className="tx-muted" data-testid="tx-origin-unavailable">{SENTENCES.noOrigin}</p>;
  const ledgerMinted = o.source === 'ledger' || o.minted_by === 'ledger';
  const zone = model.timezone;
  const https = typeof o.gcal_link === 'string' && /^https:\/\//i.test(o.gcal_link);
  return (
    <section className="tx-section" aria-label="Where it came from" data-testid="tx-origin">
      <h4>Where it came from</h4>
      <ul>
        <li data-testid="tx-origin-source">{originSourceText(o)}</li>
        {o.source === 'gcal' && o.calendar ? <li>Calendar: {o.calendar}</li> : null}
        {o.room ? <li>Room: {o.room}</li> : null}
        {o.source === 'gcal' && https ? <li><a href={o.gcal_link} target="_blank" rel="noopener noreferrer">Open in Google Calendar</a></li> : null}
        {o.captured_at ? <li>Booked {formatTxTime(o.captured_at, zone)}</li> : null}
        {typeof o.raw_price_text === 'string' && o.raw_price_text !== '' ? <li><PriceText value={o.raw_price_text} /></li> : null}
        {o.series ? <li data-testid="tx-origin-series">Part of: {o.series.title}, {o.series.recurrence_summary}</li> : null}
        {o.instance ? (
          <li data-testid="tx-origin-instance">
            This session{o.instance.ordinal ? `: #${o.instance.ordinal}` : ''}{o.instance.original_start ? ', changed from the series' : ''}
            {` (${formatTxTime(o.instance.start, zone)} - ${formatTxTime(o.instance.end, zone)})`}
          </li>
        ) : null}
      </ul>
    </section>
  );
}

function PriceSection({ model }) {
  const p = model.pricing;
  const ledgerMinted = model.origin && (model.origin.source === 'ledger' || model.origin.minted_by === 'ledger');
  if (ledgerMinted) return <section className="tx-section" data-testid="tx-price"><h4>Price</h4><p>Not applicable: there is no Core booking.</p></section>;
  if (!p) return null;
  return (
    <section className="tx-section" aria-label="Price" data-testid="tx-price">
      <h4>Price</h4>
      <ul>
        {model.canViewAmounts && p.amount_minor != null ? <li data-testid="tx-price-amount">{amountText(p.amount_minor, p.currency || undefined)}</li> : null}
        {!model.canViewAmounts ? <li>Amounts are hidden for your access: {model.can?.view_amounts?.reason || ''}</li> : null}
        {p.plan ? <li>Plan: {p.plan}</li> : null}
        {p.basis ? <li>Basis: {p.basis}</li> : null}
        {(Array.isArray(p.overrides) ? p.overrides : []).map((text) => <li key={text}>{text}</li>)}
      </ul>
    </section>
  );
}

function WhereNow({ model }) {
  const sentence = stageSentence({
    stage: model.stage, detail: model.stageDetail, headline: model.headline, txId: model.txId,
    ctx: {
      amount: model.canViewAmounts ? amountText(model.pricing?.amount_minor, model.pricing?.currency || undefined) : '',
      invoiceNumber: model.invoice?.number || '', entry: model.ledgerEntryId || '',
    },
  });
  const failure = FAILURE_STAGES.includes(model.stage);
  const consistency = model.consistency || {};
  return (
    <section className="tx-section" aria-label="Where it is now" data-testid="tx-where-now">
      <h4>Where it is now</h4>
      <p><span className={`tx-stage-pill${failure ? ' pill-problem' : ''}`} data-testid="tx-stage-pill" data-stage={model.stage}>{stageLabel(model.stage, model.stageDetail)}</span></p>
      {model.ledgerOnly ? null : <p className="tx-headline" data-testid="tx-headline">{sentence.text}</p>}
      {!model.ledgerOnly && sentence.fallback ? <p className="tx-muted">(no description supplied)</p> : null}
      {model.from ? <p className="tx-from" data-testid="tx-from">{model.from}</p> : null}
      {model.nextAction ? <p className="tx-next" data-testid="tx-next-action">{model.nextAction}</p> : null}
      {failure && !model.nextAction && !model.ledgerOnly ? <p className="tx-next" data-testid="tx-next-missing">{`Core did not say what to do next. Contact an operator and quote ${model.txId}.`}</p> : null}
      {model.conditions.map((c) => (
        <div key={c.code} className="tx-condition" role="status" data-testid="tx-condition" data-code={c.code}>
          <span className="tx-condition-badge">{conditionBadge(c.code)}</span>
          <p className="tx-condition-message">{c.message}</p>
          {c.next_action ? <p className="tx-condition-next">{c.next_action}</p> : null}
        </div>
      ))}
      {consistency.ok === false && consistency.issue === 'ledger_unknown' ? <p className="tx-muted" data-testid="tx-consistency">The Ledger does not know this transaction. It is listed in Problems.</p> : null}
      {consistency.ok === false && consistency.issue === 'transient_stage_overdue' ? <p className="tx-muted" data-testid="tx-consistency">{`This has been in ${stageLabel(model.stage, model.stageDetail)} longer than expected. It is listed in Problems.`}</p> : null}
    </section>
  );
}

// `inlineEdit`: the drawer's Details section carries the one Edit form (WebUI #167), so the history drawer's own 'Change the amount' link is not offered.
function ActionRows({ model, orgId, onNavigate, canRaiseCredit, onRaiseCredit, customerUserId, onBilledMore, moneyAllowed = false, inlineEdit = false }) {
  // ML001-F0239: every row here is a money action (move, amount, approve, retry, credit note, bill more). Not rendered unless allowed.
  if (moneyAllowed !== true) return null;
  if (!model.can && !ledgerWritten(model)) return null;
  const rows = model.can ? ACTION_ROWS.filter((row) => row.stages.includes(model.stage) && !(inlineEdit && row.key === 'edit_amount')) : [];
  // M004-F0721 (owner/Core-lead ruling 2026-10-07): "Raise credit note" is gated on the LEDGER half (written and not paid, and the
  // viewer holds ledger_manage, which is what makes the Ledger half available), NEVER on can.edit_amount. Core keeps
  // can.edit_amount honest (allowed:false once written) and its reason is shown beside the action. The credit op's own answers
  // (nothing_to_credit, LEDGER_CREDIT_EXCEEDS_LINE, LEDGER_INVOICE_NOT_CREDITABLE) remain the real gate.
  const written = ledgerWritten(model) && canRaiseCredit;
  const creditOffered = written && creditNoteOffered(model);
  if (!rows.length && !written) return null;
  return (
    <section className="tx-section" aria-label="Actions" data-testid="tx-actions">
      <h4>Actions</h4>
      <ul>
        {rows.map((row) => {
          const can = model.can[row.key] || { allowed: false, reason: null };
          const label = row.label(model.stage);
          return (
            <li key={row.key} data-testid={`tx-action-${row.key}`} data-allowed={can.allowed ? 'true' : 'false'}>
              {can.allowed
                ? <Link to={row.to(orgId)} onClick={onNavigate}>{label}</Link>
                : <span aria-disabled="true">{label}</span>}
              {!can.allowed && can.reason ? <span className="tx-action-reason" data-testid="tx-action-reason"> {can.reason}</span> : null}
            </li>
          );
        })}
        {creditOffered ? (
          <li data-testid="tx-action-credit">
            <button type="button" className="tx-link-button" onClick={onRaiseCredit}>Raise credit note</button>
            {model.can?.edit_amount && !model.can.edit_amount.allowed && model.can.edit_amount.reason
              ? <span className="tx-action-reason" data-testid="tx-action-reason"> {model.can.edit_amount.reason}</span> : null}
          </li>
        ) : null}
        {written ? (
          <li data-testid="tx-action-bill-more">
            <TxBillMore orgId={orgId} userId={customerUserId} handOffId={model.handOffId} description={model.title} onDone={onBilledMore} />
          </li>
        ) : null}
      </ul>
    </section>
  );
}

// One read per (organisation, key, attempt) while it is IN FLIGHT: React StrictMode's dev double-invoke of the effect (and a
// remount mid-read) must not send a second request. Nothing is cached once the read settles (design 2.2: re-read on every open).
const inflight = new Map();
function readOnce(orgId, readKey, attempt, userId) {
  const id = `${orgId}|${readKey}|${attempt}|${userId ?? ''}`;
  if (!inflight.has(id)) {
    const pending = Promise.resolve().then(() => txHistoryApi.getHistory(orgId, readKey, userId));
    const clear = () => inflight.delete(id);
    pending.then(clear, clear);
    inflight.set(id, pending);
  }
  return inflight.get(id);
}

// WebUI #167 props: `details` (a node) renders above the history, as the drawer's Details section; `noHistory` (a line with no
// TXN yet) reads nothing from Core and says so; `headingText` replaces the 'Transaction TXN-…' heading.
export default function TxHistoryDrawer({ request, onClose, onOpenOther, onBusyChange, details = null, noHistory = false, headingText = '' }) {
  const { key, orgId, customerUserId, handOffId, action } = request;
  const readKey = handOffId || key;
  const [phase, setPhase] = useState(noHistory ? 'none' : 'loading');
  const [model, setModel] = useState(null);
  const [problem, setProblem] = useState(null); // { kind, candidates, fallbackModel, fallbackFailed }
  const [attempt, setAttempt] = useState(0);
  const [slow, setSlow] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [raising, setRaising] = useState(false);
  const [creditStatus, setCreditStatus] = useState('');
  // ML001-F0239: ledger_manage AND the transaction's customer is not the viewer. Closed until both are known.
  const moneyAllowed = useMoneyActionGate(orgId, customerUserId ?? model?.userId);
  const titleRef = useRef(null);
  const dismiss = useDismissOnOutsideClick(onClose);
  const displayTx = displayTxId({ txnId: model?.txId, txId: key, handOffId: handOffId || model?.handOffId }) || key;

  useEffect(() => { if (titleRef.current) titleRef.current.focus(); }, []);
  // The finder disables Find while a read is in flight (design 3.2).
  useEffect(() => { if (onBusyChange) onBusyChange(phase === 'loading'); }, [phase, onBusyChange]);
  useEffect(() => () => { if (onBusyChange) onBusyChange(false); }, [onBusyChange]);

  useEffect(() => {
    if (noHistory) { setPhase('none'); setModel(null); return undefined; }
    let current = true;
    setPhase('loading'); setModel(null); setProblem(null); setSlow(false); setRaising(false); setCreditStatus('');
    const slowTimer = setTimeout(() => { if (current) setSlow(true); }, 15000);
    const finish = (apply) => { if (current) { clearTimeout(slowTimer); apply(); } };
    readOnce(orgId, readKey, attempt, customerUserId).then(
      (response) => finish(() => {
        const next = normaliseTxHistory(unwrap(response));
        if (next) { setModel(next); setPhase('ok'); } else { setProblem({ kind: 'failed' }); setPhase('error'); }
      }),
      (error) => {
        const stepUp = getStepUpChallengeFromError(error);
        if (stepUp) { finish(() => setChallenge(stepUp)); return; }
        const cls = classifyHistoryError(error);
        const canFallback = (cls.kind === 'forbidden' || cls.kind === 'failed') && customerUserId != null && txIdFromHandOffId(handOffId);
        if (!canFallback) { finish(() => { setProblem(cls); setPhase('error'); }); return; }
        txHistoryApi.getLedgerHistory(orgId, customerUserId, handOffId).then(
          (response) => finish(() => {
            const ledgerModel = normaliseLedgerOnlyHistory(unwrap(response));
            if (ledgerModel) { setModel(ledgerModel); setProblem({ kind: cls.kind, fallback: true }); setPhase('ok'); } else { setProblem({ ...cls, fallbackFailed: true }); setPhase('error'); }
          }),
          () => finish(() => { setProblem({ ...cls, fallbackFailed: true }); setPhase('error'); }),
        );
      },
    );
    return () => { current = false; clearTimeout(slowTimer); };
  }, [orgId, readKey, customerUserId, handOffId, attempt, noHistory]);

  // M004-F0721 / W9: a request opened with action 'credit' (the correction form's "raise a credit note instead" link) lands with the
  // credit-note form already open, once, when the credit note is offered for this TX. Otherwise it is the plain drawer.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!moneyAllowed || action !== 'credit' || autoOpened.current || phase !== 'ok' || !model) return;
    if (!creditNoteOffered(model) || model.ledgerOnly || (customerUserId ?? model.userId) == null) return;
    autoOpened.current = true;
    setRaising(true);
  }, [moneyAllowed, action, phase, model, customerUserId]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  // After a credit note: apply the Ledger's own `state` at once (confirmed, not optimistic), then re-read quietly so Core's
  // timeline (credit_noted, credited) catches up. A failed quiet read leaves the applied state in place.
  const onCreditRaised = useCallback(({ state, message }) => {
    setModel((current) => applyLedgerState(current, state));
    setCreditStatus(message);
    setRaising(false);
    Promise.resolve().then(() => txHistoryApi.getHistory(orgId, readKey, customerUserId)).then((response) => {
      const next = normaliseTxHistory(unwrap(response));
      // A read that has not caught up must never drop a document the Ledger already confirmed: keep the union.
      if (next) setModel((current) => ({ ...next, children: mergeChildren(next.children, current?.children) }));
    }, () => {});
  }, [orgId, readKey, customerUserId]);
  // After "Bill more under this transaction" the Ledger has answered; re-read quietly (the model stays on screen) so the `corrected`
  // event and the `correction` child appear. A failed re-read leaves the page as it was; the form's own status line is the confirmation.
  const onBilledMore = useCallback(() => {
    Promise.resolve().then(() => txHistoryApi.getHistory(orgId, readKey, customerUserId)).then((response) => {
      const next = normaliseTxHistory(unwrap(response));
      if (next) setModel((current) => ({ ...next, children: mergeChildren(next.children, current?.children) }));
    }, () => {});
  }, [orgId, readKey, customerUserId]);
  const onKeyDown = (event) => { if (event.key === 'Escape') { event.stopPropagation(); onClose(); } };
  const copy = () => { try { const p = globalThis.navigator?.clipboard?.writeText?.(displayTx); if (p && p.catch) p.catch(() => {}); } catch { /* silent */ } };

  const shortKey = Boolean(wellFormedTxId(key)) && !handOffId;
  let body = null;
  if (phase === 'none') {
    body = <p className="tx-muted" data-testid="tx-none">{TX_NONE_YET}</p>;
  } else if (phase === 'loading') {
    body = (
      <div role="status" data-testid="tx-loading">
        <p>{SENTENCES.loading(displayTx)}</p>
        {slow ? <p data-testid="tx-slow">{SENTENCES.slow} <button type="button" className="tx-link-button" onClick={onClose}>Cancel</button></p> : null}
      </div>
    );
  } else if (phase === 'error') {
    const kind = problem?.kind;
    body = (
      <div data-testid="tx-error" data-kind={kind}>
        {kind === 'ambiguous' ? (
          <>
            <p role="status" data-testid="tx-ambiguous">{SENTENCES.ambiguous(displayTx)}</p>
            <ul className="tx-candidates">
              {(problem.candidates || []).map((c) => (
                <li key={c.hand_off_id} data-testid="tx-candidate">
                  <span>{[c.booking_title, c.date || formatWindow(c.window)].filter(Boolean).join(' . ')}{c.amount_minor != null ? ` . ${amountText(c.amount_minor, c.currency || undefined)}` : ''}</span>
                  <code> {displayTxId({ txId: c.tx_id, handOffId: c.hand_off_id })} {c.hand_off_id}</code>{' '}
                  <button type="button" className="btn btn-outline" onClick={() => onOpenOther({ key: c.tx_id, orgId, customerUserId, handOffId: c.hand_off_id })}>Open</button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {kind === 'not_found' ? <p role="status" data-testid="tx-not-found">{SENTENCES.notFound(displayTx)}{shortKey ? ` ${SENTENCES.notFoundLedgerHint}` : ''}</p> : null}
        {kind === 'bad_key' ? <p role="alert" data-testid="tx-bad-key">{SENTENCES.badKey}</p> : null}
        {kind === 'forbidden' ? <p role="alert" data-testid="tx-forbidden">{SENTENCES.forbidden}</p> : null}
        {kind === 'failed' ? <p role="alert" data-testid="tx-failed">{SENTENCES.failed}</p> : null}
        {problem?.fallbackFailed ? <p role="alert" data-testid="tx-ledger-failed">The Ledger could not show its part of this history either.</p> : null}
        {kind === 'failed' || problem?.fallbackFailed ? <button type="button" className="btn btn-outline" onClick={retry}>Try again</button> : null}
      </div>
    );
  } else if (model) {
    const ledgerOnly = model.ledgerOnly;
    body = (
      <>
        {ledgerOnly ? <p className="tx-notice" role="status" data-testid="tx-ledger-only">{fallbackIntro(problem?.kind)}</p> : null}
        <WhereNow model={model} />
        {creditStatus ? <p className="tx-notice" role="status" data-testid="tx-credit-status">{creditStatus}</p> : null}
        <ActionRows inlineEdit={Boolean(details)} moneyAllowed={moneyAllowed} model={model} orgId={orgId} onNavigate={onClose} canRaiseCredit={!ledgerOnly && (customerUserId ?? model.userId) != null && Boolean(model.handOffId)} onRaiseCredit={() => { setCreditStatus(''); setRaising(true); }} customerUserId={customerUserId ?? model.userId} onBilledMore={onBilledMore} />
        {moneyAllowed && raising ? (
          <TxCreditNoteForm
            orgId={orgId}
            userId={customerUserId ?? model.userId}
            handOffId={model.handOffId}
            txId={model.txId}
            remainingMinor={remainingToCreditMinor(model)}
            currency={model.pricing?.currency}
            onRaised={onCreditRaised}
            onCancel={() => setRaising(false)}
          />
        ) : null}
        {ledgerOnly ? <p className="tx-muted" data-testid="tx-origin-unavailable">{SENTENCES.noOrigin}</p> : <OriginSection model={model} />}
        {ledgerOnly ? null : <PriceSection model={model} />}
        <TxInvoiceAndLedger history={model} />
        <h4>Timeline</h4>
        <TxTimeline history={model} onRetry={retry} />
        <button type="button" className="btn btn-outline" onClick={retry} data-testid="tx-refresh">Refresh</button>
      </>
    );
  }

  const heading = model?.title || '';
  const title = headingText || `Transaction ${displayTx}`;
  return (
    <div className="bkh-drawer-backdrop" role="presentation" {...dismiss}>
      <aside
        className="bkh-drawer bkh-drawer-wide tx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tx-drawer-title"
        data-testid="tx-history-drawer"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <StepUpDialog
          challenge={challenge}
          title="Verify to see this history"
          description="Confirm your identity before reading this transaction's history."
          onComplete={() => { setChallenge(null); retry(); }}
          onCancel={() => { setChallenge(null); setProblem({ kind: 'forbidden' }); setPhase('error'); }}
        />
        <div className="bkh-drawer-header">
          <div>
            <h2 id="tx-drawer-title" ref={titleRef} tabIndex={-1}>{title}</h2>
            {noHistory ? null : <button type="button" className="tx-chip-copy" aria-label={`Copy ${displayTx}`} onClick={copy}>Copy</button>}
            {heading ? <p className="tx-title" data-testid="tx-title">{heading}</p> : null}
            {model?.room?.name ? <p className="tx-room">{model.room.name}</p> : null}
            {model?.window ? <p className="tx-window" data-testid="tx-window">{formatWindow(model.window)}</p> : null}
            {model?.parentHandOffId ? <p className="tx-muted" data-testid="tx-parent">Linked to {displayTxId({ handOffId: model.parentHandOffId })}</p> : null}
          </div>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
        <div className="tx-drawer-body">{details ? <div className="tx-item-details" data-testid="tx-item-details">{details}</div> : null}{body}</div>
      </aside>
    </div>
  );
}
